import { clientV3SDK } from "@istari-frontend/api-client";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { QUERY_REFETCH_INTERVAL_MS_30S, QUERY_STALE_TIME_MS_30S } from "~/constants";
import type { NotificationStatus } from "~/notifications/model/notification";

import { hasInbox, orNullWithoutInbox } from "./inbox-availability";

export type NotificationFilter = {
  status?: NotificationStatus;
};

export const notificationQueryKeys = {
  // Intentionally separate from polling keys ["notifications", "list"] to prevent inbox
  // mutations from advancing the toast pipeline's server-side cursor via React Query partial-match invalidation.
  all: ["inbox"] as const,
  list: (filter?: NotificationFilter) => [...notificationQueryKeys.all, "list", filter] as const,
  byId: (id: string) => [...notificationQueryKeys.all, id] as const,
  unreadCount: () => [...notificationQueryKeys.all, "unreadCount"] as const,
};

function toSdkFilter(filter?: NotificationFilter) {
  if (!filter?.status) return { archived: false };
  switch (filter.status) {
    case "unread":
      return { read: false, archived: false };
    case "read":
      return { read: true, archived: false };
    case "archived":
      return { archived: true };
  }
}

export const notificationQueryOptions = {
  list: (filter?: NotificationFilter) =>
    infiniteQueryOptions({
      queryKey: notificationQueryKeys.list(filter),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        clientV3SDK.listInboxItems({ ...toSdkFilter(filter), cursor: pageParam }),
      getNextPageParam: (page) => page.nextCursor ?? undefined,
      initialPageParam: undefined as string | undefined,
      refetchInterval: QUERY_REFETCH_INTERVAL_MS_30S,
    }),
  byId: (id: string) =>
    queryOptions({
      queryKey: notificationQueryKeys.byId(id),
      queryFn: () => clientV3SDK.getInboxItem({ itemId: id }),
      enabled: !!id,
    }),
  unreadCount: () =>
    queryOptions({
      queryKey: notificationQueryKeys.unreadCount(),
      queryFn: () => orNullWithoutInbox(clientV3SDK.listInboxItems({ size: 1, archived: false })),
      enabled: hasInbox,
      staleTime: QUERY_STALE_TIME_MS_30S,
    }),
};
